import { I18n } from '@calendly/i18n';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, forwardRef } from 'react';
const OFFSET = 50;
const TRIGGER_DELAY = 500;
const tooltipContainerHasRightConflictCss = "t2pv2o1";
const tooltipContainerHasBottomConflictCss = "toze5pw";
const tooltipContainerAnchorTopCss = "t1232f66";
const tooltipContainerCss = "t190f0v4";
const TooltipContainer = forwardRef((props, ref) => {
  const {
    hasRightConflict,
    hasBottomConflict,
    anchorTop,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tooltipContainerCss, {
    [tooltipContainerHasRightConflictCss]: hasRightConflict,
    [tooltipContainerHasBottomConflictCss]: hasBottomConflict,
    [tooltipContainerAnchorTopCss]: anchorTop
  }, props.className)} />;
});
const tooltipBackgroundCss = "tw6uxtc";
const TooltipBackground = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tooltipBackgroundCss, props.className)} />;
});
const listCss = "l1ydqo40";
const List = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'ul';
  return <Component ref={ref} {...restProps} className={classNames(listCss, props.className)} />;
});
const listItemCss = "l1boh151";
const ListItem = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'li';
  return <Component ref={ref} {...restProps} className={classNames(listItemCss, props.className)} />;
});
const findScrollableParent = target => {
  for (let currentNode = target.parentNode; target.name !== 'body'; currentNode = currentNode.parentNode) {
    if (window.getComputedStyle(currentNode).overflow !== 'visible') return currentNode;
  }
  return null;
};
export const useInviteeVoteTooltip = ({
  invitees,
  anchorTop = false
}) => {
  const timeoutRef = useRef();
  const [isTooltipShown, setTooltipShown] = useState(false);
  const [hasBottomConflict, setHasBottomConflict] = useState(false);
  const [hasRightConflict, setHasRightConflict] = useState(false);
  const tooltipRef = useRef();
  const isPhone = useDeviceType('isPhone');
  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;
    const spotPickerContainer = findScrollableParent(tooltip);
    if (!spotPickerContainer) return;
    const {
      bottom,
      right
    } = tooltip.getBoundingClientRect();
    const {
      bottom: spotPickerBottom,
      right: spotPickerRight
    } = spotPickerContainer.getBoundingClientRect();
    if (!hasRightConflict) setHasRightConflict(Math.round(right) > spotPickerRight);
    if (!hasBottomConflict) setHasBottomConflict(Math.round(bottom) > spotPickerBottom - OFFSET);
  }, [isTooltipShown, tooltipRef, hasRightConflict, hasBottomConflict]);
  const showTooltip = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setTooltipShown(true);
    }, TRIGGER_DELAY);
  }, []);
  const hideTooltip = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setTooltipShown(false);
  }, []);
  useEffect(() => () => {
    clearTimeout(timeoutRef.current);
  }, []);
  useEffect(() => {
    if (invitees.length === 0) hideTooltip();
  }, [hideTooltip, invitees]);
  const TooltipComponent = useCallback(() => {
    if (!isTooltipShown || isPhone || !invitees?.length) return null;
    return <TooltipContainer role="tooltip" anchorTop={anchorTop} hasBottomConflict={hasBottomConflict} hasRightConflict={hasRightConflict} onClick={e => e.stopPropagation()} data-testid="invitee-vote-tooltip">
        <TooltipBackground ref={tooltipRef}>
          <List>
            {invitees.map(invitee => <ListItem key={invitee.id}>
                {invitee.fullName}
              </ListItem>)}
          </List>
        </TooltipBackground>
      </TooltipContainer>;
  }, [isTooltipShown, isPhone, anchorTop, hasBottomConflict, hasRightConflict, invitees]);
  const hostProps = {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    onClick: hideTooltip,
    'aria-label': I18n.t('publisher.meetings.best_times.invitee_votes_tooltip')
  };
  return [TooltipComponent, hostProps];
};

require("./invitee_vote_tooltip.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitee_vote_tooltip.js");